import _extends from '@babel/runtime/helpers/extends';
import _objectWithoutPropertiesLoose from '@babel/runtime/helpers/objectWithoutPropertiesLoose';
import * as React from 'react';
import { useContext as useContext$1, useRef } from 'react';
import { StyleSheet, css } from 'aphrodite';

function flatten(list) {
  const result = [];

  if (!list) {
    return result;
  } else if (Array.isArray(list)) {
    for (const item of list) {
      result.push.apply(result, flatten(item));
    }
  } else {
    result.push(list);
  }

  return result;
}

function processStyleList(style) {
  const stylesheetStyles = [];
  const inlineStyles = [];

  if (!style) {
    return {
      style: {},
      className: ""
    };
  }

  const shouldInlineStyles = typeof global !== "undefined" && global.SNAPSHOT_INLINE_APHRODITE;
  flatten(style).forEach(child => {
    const _definition = child._definition;

    if (_definition != null) {
      if (shouldInlineStyles) {
        const def = {};

        for (const [key, value] of Object.entries(_definition)) {
          def[key.replace(/-[a-z]/g, match => match[1].toUpperCase())] = value;
        }

        inlineStyles.push(def);
      } else {
        stylesheetStyles.push(child);
      }
    } else {
      inlineStyles.push(child);
    }
  });
  const inlineStylesObject = Object.assign.apply(Object, [{}].concat(inlineStyles));

  if (inlineStyles.length > 0 && !shouldInlineStyles) {
    const inlineStylesStyleSheet = StyleSheet.create({
      inlineStyles: inlineStylesObject
    });
    stylesheetStyles.push(inlineStylesStyleSheet.inlineStyles);
  }

  return {
    style: shouldInlineStyles ? inlineStylesObject : {},
    className: css.apply(void 0, stylesheetStyles)
  };
}

const _excluded$2 = ["children", "style", "tag", "testId"];
const isHeaderRegex = /^h[1-6]$/;
const styles$1 = StyleSheet.create({
  text: {
    WebkitFontSmoothing: "antialiased",
    MozOsxFontSmoothing: "grayscale"
  },
  header: {
    marginTop: 0,
    marginBottom: 0
  }
});
class Text extends React.Component {
  render() {
    const _this$props = this.props,
          {
      children,
      style,
      tag: Tag,
      testId
    } = _this$props,
          otherProps = _objectWithoutPropertiesLoose(_this$props, _excluded$2);

    const isHeader = isHeaderRegex.test(Tag);
    const styleAttributes = processStyleList([styles$1.text, isHeader && styles$1.header, style]);
    return React.createElement(Tag, _extends({}, otherProps, {
      style: styleAttributes.style,
      className: styleAttributes.className,
      "data-test-id": testId
    }), children);
  }

}
Text.defaultProps = {
  tag: "span"
};

const _excluded$1 = ["className", "style"];
function addStyle(Component, defaultStyle) {
  function StyleComponent(props) {
    const {
      className,
      style
    } = props,
          tmpOtherProps = _objectWithoutPropertiesLoose(props, _excluded$1);

    const otherProps = tmpOtherProps;
    const reset = typeof Component === "string" ? overrides[Component] : null;
    const {
      className: aphroditeClassName,
      style: inlineStyles
    } = processStyleList([reset, defaultStyle, style]);
    return React.createElement(Component, _extends({}, otherProps, {
      className: [aphroditeClassName, className].filter(Boolean).join(" "),
      style: inlineStyles
    }));
  }

  return StyleComponent;
}
const overrides = StyleSheet.create({
  button: {
    margin: 0,
    "::-moz-focus-inner": {
      border: 0
    }
  }
});

const _excluded = ["testId", "tag"];
const styles = StyleSheet.create({
  default: {
    alignItems: "stretch",
    borderWidth: 0,
    borderStyle: "solid",
    boxSizing: "border-box",
    display: "flex",
    flexDirection: "column",
    margin: 0,
    padding: 0,
    position: "relative",
    zIndex: 0,
    minHeight: 0,
    minWidth: 0
  }
});
const StyledDiv = addStyle("div", styles.default);
const StyledArticle = addStyle("article", styles.default);
const StyledAside = addStyle("aside", styles.default);
const StyledNav = addStyle("nav", styles.default);
const StyledSection = addStyle("section", styles.default);
class View extends React.Component {
  render() {
    const _this$props = this.props,
          {
      testId,
      tag
    } = _this$props,
          restProps = _objectWithoutPropertiesLoose(_this$props, _excluded);

    const props = _extends({}, restProps, {
      "data-test-id": testId
    });

    switch (tag) {
      case "article":
        return React.createElement(StyledArticle, props);

      case "aside":
        return React.createElement(StyledAside, props);

      case "nav":
        return React.createElement(StyledNav, props);

      case "section":
        return React.createElement(StyledSection, props);

      case "div":
        return React.createElement(StyledDiv, props);

      default:
        throw Error(`${tag} is not an allowed value for the 'tag' prop`);
    }
  }

}
View.defaultProps = {
  tag: "div"
};

const RenderState = require("flow-enums-runtime")({
  Root: "root",
  Initial: "initial",
  Standard: "standard"
});
const RenderStateContext = React.createContext(RenderState.Root);

class WithSSRPlaceholder extends React.Component {
  constructor(...args) {
    super(...args);
    this.state = {
      mounted: false
    };
    this._isTheRootComponent = false;
  }

  componentDidMount() {
    if (this._isTheRootComponent) {
      this.setState({
        mounted: true
      });
    }
  }

  _renderAsRootComponent() {
    const {
      mounted
    } = this.state;
    const {
      children,
      placeholder
    } = this.props;
    this._isTheRootComponent = true;

    if (mounted) {
      return React.createElement(RenderStateContext.Provider, {
        value: RenderState.Standard
      }, children());
    }

    if (placeholder) {
      return React.createElement(RenderStateContext.Provider, {
        value: RenderState.Initial
      }, placeholder());
    }

    return null;
  }

  _maybeRender(renderState) {
    const {
      children,
      placeholder
    } = this.props;

    switch (renderState) {
      case RenderState.Root:
        return this._renderAsRootComponent();

      case RenderState.Initial:
        if (placeholder) {
          return placeholder();
        }

        return null;

      case RenderState.Standard:
        return children();
    }

    {
      console.log(`We got a render state we don't understand: "${JSON.stringify(renderState)}"`);
      return this._maybeRender(RenderState.Root);
    }
  }

  render() {
    return React.createElement(RenderStateContext.Consumer, null, value => this._maybeRender(value));
  }

}

class UniqueIDFactory {
  constructor(scope) {
    this.get = key => {
      const normalizedKey = key.toLowerCase();

      if (!this._hasValidIdChars(key)) {
        throw new Error(`Invalid identifier key: ${key}`);
      }

      return `${this._uniqueFactoryName}-${normalizedKey}`;
    };

    scope = typeof scope === "string" ? scope : "";
    const normalizedScope = scope.toLowerCase();

    if (!this._hasValidIdChars(normalizedScope)) {
      throw new Error(`Invalid factory scope: ${scope}`);
    }

    this._uniqueFactoryName = `uid-${normalizedScope}-${UniqueIDFactory._factoryUniquenessCounter++}`;
  }

  _hasValidIdChars(value) {
    if (typeof value !== "string") {
      return false;
    }

    const invalidCharsReplaced = value.replace(/[^\d\w-]/g, "-");
    return value === invalidCharsReplaced;
  }

}
UniqueIDFactory._factoryUniquenessCounter = 0;

class SsrIDFactory {
  get(id) {
    return id;
  }

}

SsrIDFactory.Default = new SsrIDFactory();
var SsrIDFactory$1 = SsrIDFactory.Default;

class UniqueIDProvider extends React.Component {
  _performRender(firstRender) {
    const {
      children,
      mockOnFirstRender,
      scope
    } = this.props;

    if (firstRender) {
      if (mockOnFirstRender) {
        return children(SsrIDFactory$1);
      }

      return null;
    }

    if (!this._idFactory) {
      this._idFactory = new UniqueIDFactory(scope);
    }

    return children(this._idFactory);
  }

  render() {
    return React.createElement(WithSSRPlaceholder, {
      placeholder: () => this._performRender(true)
    }, () => this._performRender(false));
  }

}

class IDProvider extends React.Component {
  renderChildren(ids) {
    const {
      id,
      children
    } = this.props;
    const uniqueId = ids ? ids.get(IDProvider.defaultId) : id;

    if (!uniqueId) {
      throw new Error("Did not get an identifier factory nor a id prop");
    }

    return children(uniqueId);
  }

  render() {
    const {
      id,
      scope
    } = this.props;

    if (id) {
      return this.renderChildren();
    } else {
      return React.createElement(UniqueIDProvider, {
        scope: scope,
        mockOnFirstRender: true
      }, ids => this.renderChildren(ids));
    }
  }

}
IDProvider.defaultId = "wb-id";

let serverSide = false;
var server = {
  isServerSide: () => serverSide,
  setServerSide: () => {
    serverSide = true;
  }
};

const useUniqueIdWithMock = scope => {
  const renderState = useContext$1(RenderStateContext);
  const idFactory = useRef(null);

  if (renderState === RenderState.Root) {
    throw new Error("Components using useUniqueIdWithMock() should be descendants of <RenderStateRoot>");
  }

  if (renderState === RenderState.Initial) {
    return SsrIDFactory$1;
  }

  if (!idFactory.current) {
    idFactory.current = new UniqueIDFactory(scope);
  }

  return idFactory.current;
};
const useUniqueIdWithoutMock = scope => {
  const renderState = useContext$1(RenderStateContext);
  const idFactory = useRef(null);

  if (renderState === RenderState.Root) {
    throw new Error("Components using useUniqueIdWithoutMock() should be descendants of <RenderStateRoot>");
  }

  if (renderState === RenderState.Initial) {
    return null;
  }

  if (!idFactory.current) {
    idFactory.current = new UniqueIDFactory(scope);
  }

  return idFactory.current;
};

const useForceUpdate = () => {
  const [, setState] = React.useState(false);
  const forceUpdate = React.useCallback(() => setState(state => !state), []);
  return forceUpdate;
};

const {
  useContext,
  useEffect,
  useState
} = React;
const RenderStateRoot = ({
  children,
  throwIfNested
}) => {
  const [firstRender, setFirstRender] = useState(true);
  const contextValue = useContext(RenderStateContext);
  useEffect(() => {
    setFirstRender(false);
  }, []);

  if (contextValue !== RenderState.Root) {
    if (throwIfNested) {
      throw new Error("There's already a <RenderStateRoot> above this instance in " + "the render tree.  This instance should be removed.");
    }

    return children;
  }

  const value = firstRender ? RenderState.Initial : RenderState.Standard;
  return React.createElement(RenderStateContext.Provider, {
    value: value
  }, children);
};
RenderStateRoot.defaultProps = {
  throwIfNested: true
};

export { IDProvider, RenderStateRoot, server as Server, Text, UniqueIDProvider, View, WithSSRPlaceholder, addStyle, useForceUpdate, useUniqueIdWithMock, useUniqueIdWithoutMock };
